import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, Outlet, redirect } from '@tanstack/react-router'

import { ErrorView } from '#admin/components/error-view.tsx'
import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { movedPath } from '#admin/lib/moved-paths.ts'

/**
 * The root route.
 *
 * It owns nothing but the outlet and the error boundary — **no business behavior**, which is
 * the guide's central rule: `routes/` says where a user can go, `features/` says what the
 * product does. Anything here would be reachable from every page and owned by none.
 *
 * It also sends an old URL to the page's new URL (`lib/moved-paths.ts`). The root is the only
 * route that runs for a path that no longer matches a page. The search and the hash stay.
 */
export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  beforeLoad: ({ location }) => {
    const to = movedPath(location.pathname)
    if (to === null) return
    throw redirect({ href: to + location.href.slice(location.pathname.length), replace: true })
  },
  component: Outlet,
  // Only for what fails before the console frame exists — the session or the manifest themselves.
  // Everything below renders the router defaults inside the frame instead (`router.tsx`).
  errorComponent: ({ error, reset }) => <ErrorView error={error} reset={reset} />,
  notFoundComponent: NotFoundView,
})
